import { retrieveFile, createResponse } from "./cloudFunctions"
import {
  getModel,
  FUNCTION_GET_BATCH_DESCRIPTIONS,
  QTY_SELECTION_ANY,
  QTY_SELECTION_UPTO,
  QTY_SELECTION_FIXED,
  GPT_5,
} from "./chatGenerationServices"
import {
  createChatPrompt,
  createPromptDataFromModelCache,
} from "./chatPromptServices"
import { createChatPromptData } from "./modelEditServices"
import * as palette from "../../components/symbols/palette"
import { addIds, getRootElements } from "./modelEditServices"
import { createModelCacheKey, searchModelCache } from "./modelServices"
import db from "../../Firestore"
import { AIM_AI } from "./roleServices"

const AVERAGE_CHARS_PER_WORD = 6

const createContent = async ({
  accountId,
  modelCache,
  viewSet,
  views,
  // Optional parent element under which content is generated
  currentElement,
  currentView,
  maxElementId,
  levelSpecs,
  elementDefinitions,
  handlePasteAdd,
  // This is the text response that accompanies the diagram
  setTextResponse,
  setWaitingElementIds,
  stopRequested,
  setGeneratingContentMessage,
  roles,
  //vectorStores,
  // Files selected to be used in this prompt
  viewSetFiles,
  // GPT model passed in as per selection from user from UI
  gptModel,
  // true => normal ArchiMate element based response, false => plain text response not tied to ArchiMate elements
  useElements,
  // true => elements should be driven off the text response, false => elements should be created independently of the text response
  alignElements,
}) => {
  console.log("%cGenerating content", "color:lightgreen", {
    levelSpecs,
    currentElement,
    currentView,
    maxElementId,
    gptModel,
  })

  const root = []

  let currentMaxId = maxElementId

  let prevResult

  let pastedElements

  let theView = { ...currentView }

  for (const [index, levelSpec] of levelSpecs.entries()) {
    if (stopRequested.value === true) {
      console.log("stop requested")
      return { error: "stop requested" }
    }

    const { viewPrompts, missingViews } = await getPromptsForReferencedViews({
      accountId,
      views,
      modelCache,
      prompt: levelSpec.info,
    })

    const loadedViewRefs = await loadDesignViewRefs({
      viewRefs: missingViews,
      accountId,
    })

    const designViewPrompts = loadedViewRefs.map((v) => ({
      src: v.src,
      prompt: createViewPrompt({
        view: v.view,
      }),
    }))

    const allViewPrompts = [...viewPrompts, ...designViewPrompts]

    if (index === 0) {
      setGeneratingContentMessage(
        `Generate level ${index + 1} of ${levelSpecs.length}`
      )

      if (currentElement) {
        setWaitingElementIds((curr) => [...curr, currentElement.id])
      }

      console.log("%clevelSpec", "color:pink", { levelSpec })

      const nextLevel = await createElementLevel({
        topLevel: true,
        referencedViewPrompts: allViewPrompts,
        // 'item' may or may not be set
        parentElement: currentElement,
        currentView: theView,
        //levelSpec: levelSpecWithChildrenAttr,
        levelSpec: levelSpec,
        elementDefinitions,
        handlePasteAdd,
        setWaitingElementIds,
        root,
        viewSet,
        model: gptModel,
        //vectorStores,
        viewSetFiles,
        roles,
        setTextResponse,
        useElements,
        alignElements,
      })

      if (nextLevel.error) {
        return nextLevel
      }

      theView = { ...theView, elements: nextLevel.pastedElements }

      if (nextLevel.text_response) {
        theView.text_response = nextLevel.text_response
      }

      //console.log("%cGenerated next level", "color:yellow", nextLevel)

      prevResult = nextLevel

      if (currentElement) {
        setWaitingElementIds((curr) =>
          curr.filter((id) => id !== currentElement.id)
        )
      }

      currentMaxId = addIds(nextLevel.json, currentMaxId)

      pastedElements = handlePasteAdd({
        elementDataToAdd: nextLevel.json,
        parent: currentElement,
        viewElements: theView.elements,
        textResponse: nextLevel.text_response,
      })
    } else {
      const leafNodes = getLeafNodes(prevResult.json)

      // Create element level for all leaf nodes
      const nextLevels = []

      // Take a deep copy of root so when we call getAllSiblings it isn't affected by the loop below updating the 'children' attribute of elements

      //const rootDeepCopy = JSON.parse(JSON.stringify(root))

      setGeneratingContentMessage(
        `Generate level ${index + 1} of ${levelSpecs.length}`
      )

      for (const [index, leafNode] of leafNodes.entries()) {
        setWaitingElementIds((curr) => [...curr, leafNode.id])

        const { viewPrompts, missingViews } =
          await getPromptsForReferencedViews({
            accountId,
            views,
            modelCache,
            prompt: levelSpec.info,
          })

        const loadedViewRefs = await loadDesignViewRefs({
          viewRefs: missingViews,
          accountId,
        })

        const designViewPrompts = loadedViewRefs.map((v) => ({
          src: v.src,
          prompt: createViewPrompt({
            view: v.view,
          }),
        }))

        const allViewPrompts = [...viewPrompts, ...designViewPrompts]

        const nextLevel = await createElementLevel({
          topLevel: false,
          referencedViewPrompts: allViewPrompts,
          // Item for which breakdown is being created
          parentElement: leafNode,
          levelSpec,
          elementDefinitions,
          currentView: theView,
          handlePasteAdd,
          setWaitingElementIds,
          context: leafNode,
          root,
          viewSet,
          model: gptModel,
          // for the 2nd level and beyond, we don't allow use of vector stores for now
          //vectorStores: [],
          viewSetFiles: [],
          roles,
          useElements,
          alignElements,
        })

        if (nextLevel.error) {
          console.log("error occurred", nextLevel.error)
          return nextLevel
        }

        prevResult = nextLevel

        theView = { ...theView, elements: nextLevel.pastedElements }

        setWaitingElementIds((curr) => curr.filter((id) => id !== leafNode.id))

        leafNode.children = nextLevel.json

        //console.log("%cadded leaf node children", "color:yellow", { leafNode, root })

        nextLevels.push(nextLevel.json)
        currentMaxId = addIds(nextLevels, currentMaxId)
        pastedElements = handlePasteAdd({
          elementDataToAdd: nextLevel.json,
          parent: currentElement,
          viewElements: theView.elements,
        })
      }
    }
  }

  if (stopRequested.value === true) {
    console.log("stop requested")
    return
  }

  return pastedElements
}

const createElementLevel = async ({
  topLevel,
  referencedViewPrompts,
  parentElement,
  levelSpec,
  elementDefinitions,
  handlePasteAdd,
  currentView,
  setWaitingElementIds,
  hasStopBeenRequested,
  context,
  viewSet,
  model = GPT_5, // you can override with "gpt-5-mini" or similar if desired
  //vectorStores, // [{ vs_id }]
  viewSetFiles, // [{ id, label }] — assumed already indexed in the vector stores, we won't re-attach
  useElements,
  alignElements,
}) => {
  if (hasStopBeenRequested) {
    console.log("stop requested")
    return { error: "stop requested" }
  }

  // ---- Resolve element type prompt (from user-defined def or palette default) ----
  const typeDef = palette.getElementTypeByIndex(levelSpec.type)

  console.log("%ctypeDef", "color:lightblue", { typeDef, levelSpec })
  let elementTypePrompt = typeDef.label
  if (useElements) {
    const elementTypeDefinition = elementDefinitions.find(
      (ed) => ed.type === typeDef.name
    )
    elementTypePrompt = elementTypeDefinition
      ? elementTypeDefinition.prompt
      : typeDef.label
  }

  // ---- Vector stores (existing) ----
  // const allVectorStoreIds = Array.isArray(vectorStores)
  //   ? vectorStores.map((vs) => vs.vs_id).filter(Boolean)
  //   : []
  const tools = viewSetFiles.length
    ? [
        {
          type: "file_search",
          vector_store_ids: viewSetFiles.map((file) => file.id),
        },
      ]
    : undefined

  // ---- Quantity handling (auto-count vs fixed) ----
  if (levelSpec.qty_selection === QTY_SELECTION_UPTO) {
    const autoCountResult = await getAutoCountOfNextLevel({
      referencedViewPrompts,
      viewSet,
      levelSpec,
      parentElement,
      gptModel: model,
    })
    if (autoCountResult?.error) {
      return { error: autoCountResult.error }
    }
    levelSpec.qty_to_use = Math.min(levelSpec.qty, autoCountResult.count)
  } else {
    levelSpec.qty_to_use = levelSpec.qty
  }

  // ---- Context / sibling-avoidance prompts ----
  const baseMessages = []
  if (parentElement) {
    const parentOfSelected = currentView.elements.find((el) =>
      el.children.find((c) => c.id === parentElement.id)
    )

    if (parentOfSelected) {
      const siblingIds = parentOfSelected.children
        .map((c) => c.id)
        .filter((id) => id !== parentElement.id)
      const siblings = currentView.elements.filter((el) =>
        siblingIds.includes(el.id)
      )

      baseMessages.push(
        getAvoidSiblingOverlapPrompt({ parentElement, siblings })
      )
      baseMessages.push(
        `The parent element is '${parentElement.name}'. Do NOT include that as a child.`
      )
    } else {
      const siblings = getRootElements({
        elements: currentView.elements,
      }).filter((item) => item.id !== parentElement.id)
      baseMessages.push(
        getAvoidSiblingOverlapPrompt({ parentElement, siblings })
      )
    }
  }

  if (levelSpec.info !== "") {
    console.log("%clevelSpec.info", "color:lightgreen", {
      prompt: levelSpec.info,
      viewSetFiles,
    })
  }

  // ---- Task / rules ----
  const taskBlocks = [
    `** Task **`,
    `Provide ${levelSpec.qty_to_use} ${typeDef.name} elements that align with the definition of ${elementTypePrompt}, and provide an answer to the question: ${levelSpec.info}.`,
    ``,
    `** RULES **`,
    `- The response must be fact-based and relevant to the request.`,
    `- Do not provide a generic response. Assume the user already knows the generic answer; provide specific detail addressing this request and the overall objective.`,
    `- Responses must be specific (not vague categories).`,
    `- Provide up to ${levelSpec.qty_to_use} ${typeDef.name} elements. Do not provide too few or too many.`,
    `- You must align with the definition of ${elementTypePrompt} and the ${typeDef.name} element type, as if you are an ArchiMate expert (proper naming conventions).`,
    `- The element description (if used) must never be more than 50 words. Never list the word count in the description`,
    ``,
    `** Objective (for your reasoning, don't restate): **`,
    `'${viewSet.purpose}'.`,
  ]

  // Bring in referenced views as context snippets (for your *reasoning*, not to copy)
  if (
    Array.isArray(referencedViewPrompts) &&
    referencedViewPrompts.length > 0
  ) {
    taskBlocks.push(
      ``,
      `** Reference Context **`,
      `Understand the context information within [<context name>] brackets. Utilize this broader context to inform your response.`
    )
    referencedViewPrompts.forEach((data) => {
      taskBlocks.push(`[${data.src}]`)
      taskBlocks.push(
        Array.isArray(data.prompt)
          ? data.prompt.join("\n")
          : String(data.prompt || "")
      )
      taskBlocks.push(`[/${data.name}]`)
    })
  }

  // Add extra guidance blocks used previously
  if (
    levelSpec.attrs?.includes("description") &&
    levelSpec.element_type_reason
  ) {
    taskBlocks.push(
      `The description for the ${typeDef.name} element should align with: ${levelSpec.element_type_reason}.`
    )
  }
  if (levelSpec.attrs?.includes("description")) {
    taskBlocks.push(
      `The description for each element should be around ${levelSpec.max_words} words.`,
      `Descriptions must never be vague (e.g., "This element describes...", "This process ensures...").`,
      `Do not include meta-information (e.g., "This is a description of...").`
    )
  } else {
    taskBlocks.push(`Do not provide any description for elements.`)
  }
  taskBlocks.push(
    `The element name must never be just the ArchiMate element type name (i.e., never call it "${typeDef.name}").`
  )

  const topLevelDesc =
    levelSpec.levels === 1
      ? "actual elements of your response"
      : "top level elements of your response"
  taskBlocks.push(
    `Do not create any empty top-level element, or a top-level heading/title element. The top-level array should be the ${topLevelDesc}. Do not add any unnecessary parent element.`
  )

  // ---- Additional structured prompt content (your existing helper) ----
  const promptParts = [
    "SYSTEM RULES (follow exactly):",
    "- Output MUST be a single JSON object that validates against the provided schema (root field: 'elements').",
    "- Do NOT include any extra prose, explanations, or clarifying questions.",
    "- Make sure the element name is meaningful.",
    "",
    ...taskBlocks,
    "",
    ...baseMessages,
  ]

  // Bring in any additional, structured prompt fragments you use
  const extraPromptMsgs = createPromptMessages({
    type: pluralize(typeDef.name),
    levelSpec,
    context,
    parentElement,
  })
  extraPromptMsgs.forEach((m) => {
    if (m?.content) promptParts.push(m.content)
  })

  // If viewSetFiles are present (assumed already in vector stores), list names to cue the model
  if (Array.isArray(viewSetFiles) && viewSetFiles.length > 0) {
    promptParts.push(
      "",
      `The corpus may include files such as: ${viewSetFiles
        .map((f) => f.label)
        .join(", ")}. Use them if relevant.`
    )
  }

  const prompt = promptParts.join("\n")

  // ---- Build the JSON schema from your function-def (parameters -> schema) ----
  const levelCount = 1
  const functions = createGptFunctionsForNextLevel({
    levelSpec,
    typeDef,
    maxWords: levelSpec.max_words,
    elementTypePrompt,
    levelCount: alignElements ? levelCount : 1,
  })
  const fn = functions?.[0]
  if (!fn || !fn.parameters) {
    console.error(
      "createGptFunctionsForNextLevel did not return a function with parameters"
    )
    return {
      error: "schema-generation-failed",
      json: {},
      pastedElements: [],
      text_response: "",
    }
  }

  // Clone so we can adjust counts safely if needed
  const parameters = JSON.parse(JSON.stringify(fn.parameters))

  // If the schema has a root "elements" array, we can enforce the requested count
  try {
    const elementsArr = parameters?.properties?.elements
    if (elementsArr?.type === "array") {
      // Enforce target count as an upper bound; useElements means we actually want elements
      elementsArr.maxItems = levelSpec.qty_to_use
      // Let the model return up to that number (it already says "Provide up to N" in the prompt)
      // If you want EXACT count, also set: elementsArr.minItems = levelSpec.qty_to_use;
    }
  } catch (_) {
    // non-fatal
  }

  const jsonSchema = {
    name: fn.name || "create_elements",
    description: fn.description || `Create ${typeDef.name} elements as JSON.`,
    parameters, // tool-style parameters → Responses json schema
  }

  // ---- Call Responses ----
  const responseResult = await createResponse({
    prompt,
    model: "gpt-5-mini",
    tools,
    reasoning: { effort: "medium" }, // keep it focused without meta
    jsonSchema,
    // no temperature per your rule
    store: true,
  })

  // ---- Parse JSON output ----
  let parsedJson = []
  try {
    const outputText =
      responseResult?.response?.output_text ?? responseResult?.output_text ?? ""
    const parsed = JSON.parse(outputText)
    parsedJson = parsed?.elements || []
    console.log("%cparsed JSON", "color:lightgreen", { parsedJson })
  } catch (e) {
    console.error("Error parsing JSON", e)
    return {
      error: "parse-error",
      json: {},
      pastedElements: [],
      text_response: "",
    }
  }

  // ---- Post-process (preserve your original behavior) ----
  parsedJson.forEach((element) => addType(element, typeDef.index))
  parsedJson.forEach((element) => fixProps(element))
  parsedJson.forEach((element) => {
    if (!element.children) element.children = []
    if (!element.description) element.description = ""
  })

  const pastedElementsX = handlePasteAdd({
    elementDataToAdd: parsedJson,
    parent: parentElement,
    viewElements: currentView.elements,
  })

  return {
    json: parsedJson,
    pastedElements: pastedElementsX,
    // text_response: prepText2, // (not used here)
  }
}

/**
 * Get the messages used to generate initial text content for the next level of elements
 *
 * @param {*} param0
 * @returns
 */
const getLevelPrepMessages = ({
  elementTypePrompt,
  levelSpec,
  purpose,
  roles,
  topLevel,
  typeDef,
  useElements,
}) => {
  const message = `** Background **
  - The user is seeking to achieve this objective: ${purpose}.

  ** Task **
  - You must generate content that answers this specific question: ${
    levelSpec.info
  }.
  - You must provide a response that is centrally aligned around a set of ArchiMate ${palette.formatLabel(
    typeDef.name
  )} elements that closely align to the definition of ${palette.formatLabel(
    typeDef.name
  )} elements. 
  
  ** ${palette.formatLabel(typeDef.name)} Definition **
  - ${elementTypePrompt}

  ** Constraints **
  - The response must be fact-based and relevant to the request.
  - Do not provide a generic response. Assume the user already knows the generic answer to their question, and so provide a response which goes beyond a generic answer and contains information specifically addressing this request, and the overall objective. 
  - Provide specific responses that have specific detail and are not a category or vague/broad response.

  ** Additional Information **
  - Provide the response without including any conversation, or dialogue. You can include extra text but make it fact based and relevant to the response.
  - You can nest content but do not add an overall heading or title element to the response.
  - Only use valid markdown. Rule: You MUST NOT USE ANY HTML TAGS in the markdown.
  - When formatting responses, make content for elements is nested under them so its easy to see what content relates to which element.`

  const prepMessages = [{ role: "user", content: message }]

  if (useElements) {
    const provideUpToMsg = `Provide up to ${
      levelSpec.qty
    } ${palette.formatLabel(
      typeDef.name
    )} elements. Do not provide too few, or too many as I want the level of detail to be just right without overly summarising or overly diluting meaning.`

    switch (levelSpec.qty_selection) {
      case QTY_SELECTION_FIXED:
        prepMessages.push({
          role: "user",
          content: `Provide exactly ${levelSpec.qty} elements in total.`,
        })
        break

      case QTY_SELECTION_UPTO:
        prepMessages.push({
          role: "user",
          content: provideUpToMsg,
        })
        break

      case QTY_SELECTION_ANY:
        if (!roles.includes(AIM_AI)) {
          // Even if the user selects 'any', if they don't have the AI role, we still want to enforce a limit
          prepMessages.push({
            role: "user",
            content: provideUpToMsg,
          })
        } else {
          // unnecessary...
          // prepMessages.push({
          //   role: "user",
          //   content: `Provide whatever number of ${palette.formatLabel(
          //     typeDef.name
          //   )} elements best answers the question, i.e. a middle ground that avoids overly summarising the results, or provides unnecessary detail.`,
          // })
        }

      default:
    }
  }

  return prepMessages
}

// const getAutoCountOfNextLevel = async ({
//   referencedViewPrompts,
//   viewSet,
//   levelSpec,
//   parentElement,
//   gptModel,
// }) => {
//   if (!levelSpec.type) {
//     return 0
//   }

//   const typeDef = palette.getElementTypeByIndex(levelSpec.type)

//   const viewRefMessages = [
//     {
//       role: "system",
//       content: `You are an expert in solving for this objective: ${viewSet.scope}.`,
//     },
//     ...referencedViewPrompts.map((data, index) => ({
//       role: "system",
//       content: `[${data.src}]\n${data.prompt.join("\n")}\n[/${data.src}]`,
//     })),
//   ]
//   const messages = []

//   const msgStart = `Given the provided context, I need an accurate estimation of the ${
//     !parentElement ? "top level first level of detail " : ""
//   }number of ${palette.formatLabel(
//     typeDef.name
//   )} elements required to support the objective of '${
//     viewSet.purpose
//   }'. Please consider the entire scope, depth, and breadth of the subject matter.`

//   if (parentElement) {
//     messages.push({
//       role: "user",
//       content: `${msgStart} to break down ${
//         parentElement.name
//       } to the next logical level of detail of ${palette.formatLabel(
//         typeDef.name
//       )} elements.`,
//     })
//   } else {
//     messages.push({
//       role: "user",
//       content: `${msgStart} This first set of elements will provide the foundational pillars for subsequent, more detailed logical levels of detail, and so consider all of the content available but then summarise this into a reasonably small set of top level items.`,
//     })
//   }

//   if (referencedViewPrompts.length > 0) {
//     messages.push(...viewRefMessages)
//   }

//   if (levelSpec.info.trim() !== "") {
//     messages.push({
//       role: "user",
//       content: `The count information I require is in relation to this query: ${levelSpec.info}`,
//     })
//   }

//   if (typeDef.label) {
//     messages.push({
//       role: "user",
//       content: `The definition of the ${palette.formatLabel(
//         typeDef.name
//       )} element that you need to estimate the count is as follows: ${
//         typeDef.label
//       }`,
//     })
//   }

//   messages.push({
//     role: "user",
//     content: `Please think through what the actual correct answer, in terms of what elements you would respnd with, and tell me what the boundary min and max number of answers are of ${palette.formatLabel(
//       typeDef.name
//     )} elements${
//       parentElement
//         ? ` to break down '${parentElement.name}' (defined as '${parentElement.label}') to the next level of detail`
//         : ""
//     }.`,
//   })

//   const functions = [
//     {
//       name: "get_auto_count_of_next_level",
//       description: "Get the auto count of the next level",
//       parameters: {
//         type: "object",
//         properties: {
//           min: {
//             type: "number",
//             description:
//               "The minimum number of elements to generate at the requested logical level of detail",
//           },
//           max: {
//             type: "number",
//             description:
//               "The maximum number of elements to generate at the requested logical level of detail",
//           },
//         },
//         required: ["min", "max"],
//       },
//     },
//   ]

//   let result
//   try {
//     result = await createChatCompletionWithFunctions({
//       messages: messages,
//       model: gptModel,
//       funcs: functions,
//       function_call: { name: "get_auto_count_of_next_level" },
//     })
//     console.log("%ccount result", "color:lightgreen", result)
//   } catch (e) {
//     console.log("error", e)
//     return { error: e }
//   }
//   const response = result.data.response

//   if (result.data.error) {
//     return { error: result.data.error }
//   }

//   const funcCallResultStr = response.choices[0].message.function_call.arguments

//   const json = JSON.parse(funcCallResultStr)

//   // const responseStr = response.choices[0].message.content

//   // const json = getJSONObj(responseStr)

//   // if (isNaN(json.min) || isNaN(json.max)) {
//   //     return { error: "Invalid range", json: json }
//   // }

//   const countResult = { count: json.max, range: json }

//   console.log("count result", countResult)

//   return countResult
// }

const getAutoCountOfNextLevel = async ({
  referencedViewPrompts,
  viewSet,
  levelSpec,
  parentElement,
  gptModel,
}) => {
  if (!levelSpec.type) return 0

  const typeDef = palette.getElementTypeByIndex(levelSpec.type)
  const typeLabel = palette.formatLabel(typeDef.name)

  // Build context from referenced views (if any)
  const contextBlocks =
    (referencedViewPrompts || []).map((data) => {
      const body = Array.isArray(data.prompt)
        ? data.prompt.join("\n")
        : String(data.prompt || "")
      return `[${data.src}]\n${body}\n[/${data.src}]`
    }) || []

  const baseIntro = `Given the provided context, estimate the ${
    !parentElement ? "top level first level of detail " : ""
  }number of ${typeLabel} elements required to support the objective '${
    viewSet.purpose
  }'. Consider the entire scope, depth, and breadth of the subject matter.`

  const ask = parentElement
    ? `${baseIntro} Break down '${parentElement.name}' to the next logical level of detail of ${typeLabel} elements.`
    : `${baseIntro} This first set of elements will provide foundational pillars for subsequent, more detailed logical levels; summarize into a reasonably small top-level set.`

  const extras = []
  if (levelSpec.info?.trim()) {
    extras.push(`The count relates to this query: ${levelSpec.info.trim()}`)
  }
  if (typeDef.label) {
    extras.push(`Definition of the ${typeLabel} element: ${typeDef.label}`)
  }
  if (parentElement?.label) {
    extras.push(`Parent element definition: ${parentElement.label}`)
  }

  const prompt = [
    `Objective scope: ${viewSet.scope || "(none provided)"}`,
    ask,
    ...extras,
    "",
    ...(contextBlocks.length
      ? ["CONTEXT START", ...contextBlocks, "CONTEXT END", ""]
      : []),
    "Return ONLY a JSON object with the minimum and maximum valid counts.",
    "Both values must be non-negative integers, and max must be >= min.",
  ].join("\n")

  // JSON schema the model must satisfy
  const parameters = {
    type: "object",
    properties: {
      min: {
        type: "integer",
        minimum: 0,
        description: "Minimum number of elements",
      },
      max: {
        type: "integer",
        minimum: 0,
        description: "Maximum number of elements",
      },
    },
    required: ["min", "max"],
    additionalProperties: false,
  }

  try {
    const resp = await createResponse({
      prompt,
      model: "gpt-5-mini",
      jsonSchema: {
        name: "get_auto_count_of_next_level",
        description:
          "Estimate a reasonable min/max range for the next-level element count.",
        strict: true,
        parameters, // your helper converts parameters -> schema
      },
      reasoning: { effort: "low" },
      store: false,
    })

    const raw = resp?.response?.output_text

    const obj = JSON.parse(raw)

    const min = Number(obj?.min)
    const max = Number(obj?.max)
    if (
      !Number.isInteger(min) ||
      !Number.isInteger(max) ||
      min < 0 ||
      max < min
    ) {
      return { error: "Invalid range from model", range: obj ?? null }
    }

    const countResult = { count: max, range: { min, max } }
    console.log("count result", countResult)
    return countResult
  } catch (e) {
    console.log("error", e)
    return { error: e?.message || String(e) }
  }
}

const getAvoidSiblingOverlapPrompt = ({ parentElement, siblings }) => {
  return `As you create the next level of detail for '${
    parentElement.name
  }', be aware that its sibling elements are: ${siblings
    .map((s) => s.name)
    .join(
      ", "
    )} and so do not create elements that are too similar to these or have the same name.`
}

const getPromptsForReferencedViews = async ({
  accountId,
  views,
  modelCache,
  prompt,
  loadedViewsCache = [],
}) => {
  const viewReferences = getViewReferences({ prompt: prompt })
  const parsed = parseViewReferences({ viewReferences: viewReferences })

  // console.log("viewReferences", {
  //   parsed,
  //   viewReferences,
  //   prompt: prompt,
  //   views,
  // })

  const { loadedViews, missingViews } = await getReferencedPromptViews({
    accountId,
    prompt,
    views,
    modelCache,
    loadedViewsCache,
  })

  //console.log("%cviewPromptData", "color:yellow", { loadedViews, missingViews })

  const viewPrompts = loadedViews.map((data) => {
    //console.log("create view prompt", { data })

    if (data.type === "design") {
      const viewPrompt = createViewPrompt({ view: data.view })
      return { src: data.src, prompt: viewPrompt }
    } else if (data.type === "project" || data.type === "component") {
      //console.log("create project/component prompt", { data })

      const modelCacheKey = createModelCacheKey(data.file, data.id, data.type)
      const modelCacheItem = searchModelCache({
        modelCacheKey: modelCacheKey,
        modelCache: modelCache,
      })
      //console.log("modelCacheItem", { modelCacheItem, modelCacheKey })
      const view = modelCacheItem.model.views.find((v) => v.name === data.name)
      //console.log("view", view)
      const promptData = createPromptDataFromModelCache(modelCacheItem, view)
      //console.log("promptData", promptData)

      const prompt = createChatPrompt({
        promptData,
        promptLayers: palette.LAYERS.map((layer) => layer.name),
        includeProperties: true,
        includeDoco: true,
        includeIds: false,
      })

      return { src: data.src, prompt: prompt }
    }
  })

  //console.log("%cviewPrompts", "color:lightgreen", { viewPrompts })

  return { viewPrompts, loadedViews, missingViews }
}

const loadDesignViewRefs = async ({ viewRefs, accountId }) => {
  //console.log("load design view refs", { viewRefs })

  const parsed = parseViewReferences({
    viewReferences: viewRefs.map((viewRef) => viewRef.src),
  })

  //console.log("parsed", parsed)

  const loadPromises = parsed.map(async (viewRef) => {
    //console.log("find view set", { accountId, name: viewRef.parent_name })
    const viewSetRefs = await db
      .collection("view_sets")
      .where("account_id", "==", accountId)
      .where("name", "==", viewRef.parent_name)
      .get()
    const viewSetRef = viewSetRefs.docs[0]

    //console.log("found view set ref", { ref: viewSetRef?.data() })

    const views = await db
      .collection("views")
      .where("account_id", "==", accountId)
      .where("view_set_id", "==", viewSetRef.id)
      .get()

    const viewDoc = views.docs[0]
    const view = {
      src: viewRef.src,
      view: { id: viewDoc.id, ...viewDoc.data() },
    }
    //console.log("view", view)
    return view
  })

  const loadedViews = await Promise.all(loadPromises)
  //console.log("loadedViews", { loadedViews })
  return loadedViews
}

const createViewPrompt = ({ view }) => {
  const promptData = createChatPromptData({
    currentView: view,
    selectedItemId: undefined,
    hiddenProps: [],
  })

  console.log("promptData", promptData)

  const prompts = createChatPrompt({
    promptData,
    // All layers
    promptLayers: palette.LAYERS.map((layer) => layer.name),
    includeProperties: true,
    includeDoco: false,
    includeIds: false,
  })

  return prompts
}

const getLeafNodes = (jsonArr) => {
  console.log("%cfind leaf nodes", "color:yellow", { jsonArr })

  const result = []

  const getLeafNodesRec = (json) => {
    if (json.children && json.children.length > 0) {
      json.children.forEach((child) => getLeafNodesRec(child))
    } else {
      result.push(json)
    }
  }

  jsonArr.forEach((json) => getLeafNodesRec(json))

  return result.flat()
}

// /**
//  * Take the prep text that has been generated, either using a chat completions approach (without file inputs),
//  * or a thread approach (with file inputs), and post process it to extract the element information, so that we can
//  * use this to create the model view and have a clean set of elements to work with.
//  *
//  * @param {*} param0
//  * @returns
//  */
// const postProcessElementPrepText = async ({ prepText, levelSpec }) => {
//   let elementPrepResult

//   if (prepText) {
//     // Extract element info from prepText since the assistant doesn't seem smart enough to do so, and so
//     // will create elements call 'introduction', and 'conclusion' which are not needed.

//     console.log("%cpost-process prepText", "color:lightgreen", { prepText })

//     elementPrepResult = await createChatCompletion({
//       messages: [
//         {
//           role: "user",
//           content: `Extract only the ${palette.formatLabel(
//             palette.getElementNameByIndex(levelSpec.type)
//           )} elements from INPUT TEXT text. Do not include any introductory or conclusion text.`,
//         },
//         {
//           role: "user",
//           content: `Provide the name, description, and level for each in a nested JSON array where each array object has a level, name, and description, with markdown formatting removed, ensuring that each ${levelSpec.type} element has a corresponding set of child ${levelSpec.type} elements that support it that matches the input content provided.`,
//         },
//         {
//           role: "user",
//           content: `For the description, summarise the information that has been provided into a concise description for each ${levelSpec.type} element.`,
//         },
//         {
//           role: "user",
//           content: `For the name, remove any numbering or bullet points, and ensure that the name is a concise and clear name for the ${levelSpec.type} element.`,
//         },
//         {
//           role: "user",
//           content:
//             "Be careful not to include any introductory, heading, or conclusion text in your response, and only include the content that is relevant to the elements you are extracting.",
//         },
//         {
//           role: "user",
//           content: `Only make the JSON array nested if the input data is nested, otherwise provide a flat array of elements.`,
//         },
//         {
//           role: "user",
//           content:
//             "When numbering, top level elements should be numbered 1, 2, 3, etc. and child elements should be numbered 1.1, 1.2, 1.3, etc. under the correct parent element.",
//         },
//         {
//           role: "user",
//           content: `--- INPUT TEXT ---\n${prepText}\n--- END INPUT TEXT ---`,
//         },
//       ],
//       model: GPT_5,
//     })

//     console.log("%celement prep result", "color:orange", {
//       elementPrepResult,
//     })
//   }

//   const elementPrepText =
//     elementPrepResult?.data.response.choices[0].message.content

//   console.log("%celement prep text", "color:orange", {
//     elementPrepText,
//   })

//   return elementPrepText
// }

/**
 *
 * @param {*} count
 * @param {*} type
 * @param {*} levelSpec
 * @param {*} context
 * @param {*} item | The item for which the breakdown is being created
 * @returns
 */
const createPromptMessages = ({ type, levelSpec, context, parentElement }) => {
  const messages = []

  if (parentElement) {
    messages.push(...getNextLevelDownMessages({ levelSpec, parentElement }))
  } else {
    if (
      levelSpec.levels > 1 &&
      (levelSpec.qty_selection === QTY_SELECTION_FIXED ||
        levelSpec.qty_selection === QTY_SELECTION_UPTO)
    ) {
      messages.push({
        role: "user",
        content: `There should only be up to ${
          levelSpec.qty
        } ${palette.formatLabel(
          type
        )} elements across all array levels of the response.`,
      })
    }
  }

  //messages.push(
  // {
  //   role: "user",
  //   content: `The length of any description provided should be no more than ${levelSpec.max_words} words.`,
  // }
  // {
  //   role: "user",
  //   content:
  //     "Never create an element which is give a name of an ArchiMate element.",
  // }
  //)

  if (context) {
    messages.push({
      role: "user",
      content:
        "The parent element under which you are creating child elements is as follows and this would provide context for your response: " +
        context.name,
    })
  }

  return messages
}

// Set an 'id' value for each prop, and set type='text'
const fixProps = (element) => {
  const fixProp = (prop, propId) => {
    // See if GPT has returned props in the wrong format, e.g
    // {Type: 'Equipment', id: 1, type: 'text'}
    // instead of
    // { name: 'Type': value: 'Equipment', id: 1, type: 'text'}

    // See if expected prop attributes are there
    const { name, value, ...other } = prop
    if (!name && !value) {
      // Assume that 'other' is the name and value, and set it as such
      prop.name = Object.keys(other)[0]
      prop.value = other[prop.name]

      // Delete the 'other' attribute
      delete prop[other]

      console.log("repaired prop", prop)
    }

    prop.id = propId
    prop.type = "text"
  }

  if (element.props) {
    element.props.forEach((prop, index) => fixProp(prop, index + 1))
  }

  if (element.children) {
    element.children.forEach((child) => fixProps(child))
  }

  return element
}

const pluralize = (word) => {
  if (word.endsWith("y")) {
    return word.substring(0, word.length - 1) + "ies"
  } else {
    return word + "s"
  }
}

const addType = (element, type) => {
  element.type = type
  if (element.children) {
    element.children.forEach((child) => addType(child, type))
  }
}

const createGptFunctionsForNextLevel = ({
  levelSpec,
  typeDef,
  maxWords,
  elementTypePrompt,
  // 1 or 2 levels
  levelCount,
}) => {
  // const nameDescription = `The name of the ${palette.formatLabel(
  //   typeDef.name
  // )}. ${typeDef.label ? `The rules for the name are: ${typeDef.label}` : ""}`

  const nameDescription = `Brief but meaningful name.`

  const parameters = {
    type: "object",
    properties: {
      elements: {
        type: "array",
        items: {
          type: "object",
          properties: {
            name: {
              type: "string",
              description: nameDescription,
            },
          },
          required: ["name"],
          additionalProperties: false,
        },
      },
    },
    required: ["elements"],
    additionalProperties: false,
  }

  // Cant use minItems in strict mode
  // switch (levelSpec.qty_selection) {
  //   case QTY_SELECTION_ANY:
  //     delete parameters.properties.elements.minItems
  //     delete parameters.properties.elements.maxItems
  //     break

  //   case QTY_SELECTION_UPTO:
  //     delete parameters.properties.elements.minItems
  //     parameters.properties.elements.maxItems = levelSpec.qty_to_use
  //     break

  //   case QTY_SELECTION_FIXED:
  //     parameters.properties.elements.minItems = levelSpec.qty_to_use
  //     parameters.properties.elements.maxItems = levelSpec.qty_to_use
  //     break

  //   default:
  // }

  if (levelSpec.props.length > 0) {
    parameters.properties.elements.items.properties.props = {
      type: "array",
      items: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description: "The name of the property",
          },
          value: {
            type: "string",
            description: "The value of the property",
          },
          reason: {
            type: "string",
            description: "The reason why the value was selected",
          },
        },
        required: ["name", "value", "reason"],
      },
    }
    parameters.properties.elements.items.required.push("props")
    parameters.additionalProperties = false
  }

  // Add children as a sibling property to name, which is an array of objects each with a name, description, and type.
  if (levelCount === 2) {
    parameters.properties.elements.items.properties.children = {
      type: "array",
      //minItems: 1,
      items: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description: `The name of the child ${palette.formatLabel(
              typeDef.name
            )}`,
          },
          description: {
            type: "string",
            description: `The description of the child ${palette.formatLabel(
              typeDef.name
            )}`,
          },
        },
        required: ["name", "description"],
        additionalProperties: false,
      },
    }
    parameters.properties.elements.items.required.push("children")
    parameters.properties.elements.items.additionalProperties = false
  }

  if (levelSpec.attrs.includes("description")) {
    const descPrompt =
      typeDef.element_description_prompt ||
      `${elementTypePrompt}. DO NOT include the element type name in the description, or explain what the element type is, e.g. do not write the description in the form of 'This element means', 'This element represents', etc.`

    parameters.properties.elements.items.properties.description = {
      type: "string",
      description: descPrompt,
      //minLength: Math.max(0, parseInt(maxWords) - 5) * AVERAGE_CHARS_PER_WORD,
      //maxLength: parseInt(maxWords) * AVERAGE_CHARS_PER_WORD,
    }
    parameters.properties.elements.items.required.push("description")
  }

  const functionName = `get_elements_with_${levelSpec.attrs.join("_")}`

  console.log("%cfunction name", "color:lightgreen", functionName)

  const functions = [
    {
      name: functionName,
      description: "Get elements",
      parameters: parameters,
      strict: true,
    },
  ]

  console.log("%cfunctions", "color:orange", { functions })

  return functions
}

const getViewReferences = ({ prompt }) => {
  const viewRefRegex = /\[(.*?)\]/g
  const viewReferences = []
  let match
  while ((match = viewRefRegex.exec(prompt)) != null) {
    viewReferences.push(match[1])
  }

  // Strip any surrounding [ or ] characters from the results

  viewReferences.forEach((viewRef, index) => {
    if (viewRef.startsWith("[")) {
      viewReferences[index] = viewRef.substring(1)
    }
    if (viewRef.endsWith("]")) {
      viewReferences[index] = viewRef.substring(0, viewRef.length - 1)
    }
  })

  return viewReferences
}

const parseViewReferences = ({ viewReferences }) => {
  const parsed = viewReferences
    .map((viewRef) => {
      const parts = viewRef.split(":")
      if (parts.length > 0) {
        const type = parts[0].toLowerCase()
        switch (type) {
          case "design":
            if (parts.length === 3) {
              return {
                type: type,
                parent_name: parts[1],
                name: parts[2],
                src: viewRef,
              }
            }
            console.error(`Expecting 3 parts, found ${parts.length}`, { parts })
            return undefined

          case "project":
            if (parts.length === 4) {
              return {
                type: type,
                parent_name: parts[1],
                file: parts[2],
                name: parts[3],
                src: viewRef,
              }
            }
            console.error(`Expecting 4 parts, found ${parts.length}`, { parts })
            return undefined

          case "component":
            if (parts.length === 4) {
              return {
                type: type,
                parent_name: parts[1],
                file: parts[2],
                name: parts[3],
                src: viewRef,
              }
            }
            console.error(`Expecting 4 parts, found ${parts.length}`, { parts })
            return undefined

          default:
            console.log("Unknown view ref type", viewRef)
            return undefined
        }
      }
    })
    .filter((ref) => ref !== undefined)

  return parsed
}

/**
 * Get the views referenced in a prompt, which can reference a project, component, or design view
 *
 * @param prompt The prompt that we want to parse to check for references, i.e. [<view>]
 * @param views The views in a view set that can be referenced in a prompt
 * @param modelCache The model cache that contains the project and component models that can be referenced in a prompt
 */
const getReferencedPromptViews = async ({
  accountId,
  prompt,
  views,
  modelCache,
  loadedViewsCache,
}) => {
  const loadedViews = []
  const missingViews = []

  const viewReferences = getViewReferences({ prompt: prompt })
  const parsedViewRefs = parseViewReferences({ viewReferences: viewReferences })

  for (const [index, viewRef] of parsedViewRefs.entries()) {
    switch (viewRef.type) {
      case "design":
        const view = views.find((v) => v.name === viewRef.name)
        if (view) {
          loadedViews.push({
            src: viewRef.src,
            parent_name: viewRef.parent_name,
            type: viewRef.type,
            view: view,
          })
        } else {
          missingViews.push({
            src: viewRef.src,
            parent_name: viewRef.parent_name,
            type: viewRef.type,
            reason: "view not found",
          })
        }
        break

      case "project":
        // Check if in loadedViewsCache
        const projectLoaded = loadedViewsCache.find(
          (cacheItem) => cacheItem.src === viewRef.src
        )
        if (projectLoaded) {
          //console.log("project ref in cache", { projectLoaded })
          loadedViews.push(projectLoaded)
          break
        }

        //console.log("load project", { viewRef, modelCache })

        const projects = await db
          .collection("projects")
          .where("name", "==", viewRef.parent_name)
          .where("account_id", "==", accountId)
          .get()

        if (projects.docs.length === 0) {
          missingViews.push({
            src: viewRef.src,
            file: viewRef.file,
            parent_name: viewRef.parent_name,
            reason: "project not found in db",
          })
        } else {
          // Get doc id of projects
          const projectRef = projects.docs[0].ref
          const projectKey = createModelCacheKey(
            viewRef.file,
            projectRef.id,
            "project"
          )

          const projectModelCacheItem = searchModelCache({
            modelCacheKey: projectKey,
            modelCache: modelCache,
          })

          const baseProjectResult = {
            type: viewRef.type,
            src: viewRef.src,
            id: projectRef.id,
            file: viewRef.file,
            parent_name: viewRef.parent_name,
          }

          if (projectModelCacheItem) {
            // See if the view is present in the model cache item
            const view = projectModelCacheItem.model.views.find(
              (v) => v.name === viewRef.name
            )

            if (view) {
              loadedViews.push({
                ...baseProjectResult,
                name: viewRef.name,
                view: projectModelCacheItem,
              })
            } else {
              missingViews.push({
                ...baseProjectResult,
                reason: "project found, but view not found in project",
              })
            }
          } else {
            missingViews.push({
              ...baseProjectResult,
              reason: "project not found in cache",
            })
          }
        }

        break

      case "component":
        //console.log("load component", { viewRef })

        // Check if in loadedViewsCache
        const componentLoaded = loadedViewsCache.find(
          (cacheItem) => cacheItem.src === viewRef.src
        )
        if (componentLoaded) {
          console.log("component ref in cache", { componentLoaded })
          loadedViews.push(componentLoaded)
          break
        }

        const components = await db
          .collection("components")
          .where("name", "==", viewRef.parent_name)
          .where("account_id", "==", accountId)
          .get()

        // Get doc id of projects

        if (components.docs.length === 0) {
          missingViews.push({
            type: viewRef.type,
            src: viewRef.src,
            file: viewRef.file,
            parent_name: viewRef.parent_name,
            reason: "component not found in db",
          })
        } else {
          const componentRef = components.docs[0].ref
          const componentKey = createModelCacheKey(
            viewRef.file,
            componentRef.id,
            "component"
          )
          //console.log("%ccomponent key", "color:orange", { viewRef, componentKey })
          const componentModelCacheItem = searchModelCache({
            modelCacheKey: componentKey,
            modelCache: modelCache,
          })

          const baseComponentResult = {
            type: viewRef.type,
            src: viewRef.src,
            id: componentRef.id,
            file: viewRef.file,
            parent_name: viewRef.parent_name,
          }

          if (componentModelCacheItem) {
            //console.log("modelCacheItem", componentModelCacheItem)
            // See if the view is present in the model cache item
            const view = componentModelCacheItem.model.views.find(
              (v) => v.name === viewRef.name
            )

            if (view) {
              loadedViews.push({
                ...baseComponentResult,
                name: viewRef.name,
                view: view,
              })
            } else {
              missingViews.push({
                ...baseComponentResult,
                reason: "project found, but view not found in project",
              })
            }
          } else {
            missingViews.push({
              ...baseComponentResult,
              reason: "project not found in cache",
            })
          }
        }
        break

      default:
        console.log("Unknown ref type", viewRef.src)
    }
  }

  return { loadedViews, missingViews }
}

const getNextLevelDownMessages = ({ levelSpec, parentElement }) => {
  const messages = []

  const nextLevelTypeDef = palette.getElementTypeByIndex(levelSpec.type)

  const parentElementTypeDef = palette.getElementTypeByIndex(parentElement.type)

  const nextLevelDownStr = `From the given parent '${parentElement.name}' ${parentElementTypeDef.name}, list child elements that go one logical level deeper, avoiding extremes of detail or breadth. Each child should distinctly expand on the parent topic.`

  messages.push({
    role: "user",
    content: nextLevelDownStr,
    // content: `I want you to provide the next level of detail for '${
    //     parentElement.name
    // }', which is a '${palette.formatLabel(parentElementTypeDef.name)}'`,
  })

  messages.push({
    role: "user",
    content: `The child elements to be generated are of type '${palette.formatLabel(
      nextLevelTypeDef.name
    )}'`,
  })

  if (levelSpec.auto_qty) {
    messages.push({
      role: "user",
      content: `Think about how much content is available to use, and provide up to ${levelSpec.qty} elements.`,
    })
  } else {
    messages.push({
      role: "user",
      content: `Try to provide exactly ${levelSpec.qty} elements.`,
    })
  }

  if (parentElement.description) {
    messages.push({
      role: "user",
      content: `The description for '${parentElement.name}' is: '${parentElement.description}'`,
    })
  }

  return messages
}

// const getQuestionSubject = async ({ question }) => {
//   const getMainSubjectResult = await createChatCompletion({
//     messages: [
//       {
//         role: "user",
//         content: `From the following question, identify whether this question is about about 'how' to do something, or is asking for a set of nouns, i.e. 'what' items.
//           then within that 'how' vs 'what' determination, determine the main items that are being requested or inquired about.
//           For example, if the question is "What strategies can we implement to improve customer satisfaction?", the response should be "Customer satisfaction improvement strategies".
//           Another Example is for a question like 'How can a unified data model be developed to ensure consistency and quality', this is about asking how to do something, and so responding with process steps would be correct.,
//           Response using JSONSchema format with 2 attributes, 'type' and 'subject'. The type attribute should be either 'how' or 'what', and the subject attribute should be a string of the main items being requested.`,
//       },
//       {
//         role: "user",
//         content: question,
//       },
//     ],
//     model: GPT_5,
//   })

//   const mainSubject =
//     getMainSubjectResult.data.response.choices[0].message.content

//   return mainSubject
// }

export const getQuestionSubject = async ({ question }) => {
  const parameters = {
    type: "object",
    properties: {
      type: { type: "string", enum: ["how", "what"] },
      subject: { type: "string", minLength: 1 },
    },
    required: ["type", "subject"],
    additionalProperties: false,
  }

  const prompt = [
    "Classify the question as either 'how' (asking for steps/process) or 'what' (asking for items/nouns).",
    "Then output the main subject as a concise noun phrase.",
    "",
    "Examples:",
    `Q: What strategies can we implement to improve customer satisfaction?`,
    `A: { "type": "what", "subject": "Customer satisfaction improvement strategies" }`,
    "",
    `Q: How can a unified data model be developed to ensure consistency and quality?`,
    `A: { "type": "how", "subject": "Unified data model development" }`,
    "",
    "Return ONLY a single JSON object that validates against the schema.",
    "",
    `Question: ${question}`,
  ].join("\n")

  const resp = await createResponse({
    prompt,
    model: "gpt-5-mini",
    jsonSchema: {
      name: "get_question_subject",
      description:
        "Identify whether the question is 'how' or 'what' and extract its main subject.",
      strict: true,
      parameters, // callers use 'parameters'; helper converts to 'schema'
    },
    reasoning: { effort: "low" },
    store: false,
  })

  const raw = resp?.response?.output_text

  console.log("%craw", "color:lightgreen", { raw })

  const obj = JSON.parse(raw)

  console.log("%cparsed", "color:lightgreen", { obj })

  return obj
}

const getBatchDescription = async ({ scope, typeDef, batch }) => {
  const maxWords = 25
  const names = batch.map((e) => e.name)

  const prompt = [
    `Please create a 'description' for ${batch.length} ${typeDef.name} items.`,
    `Items:`,
    ...names.map((n) => `- ${n}`),
    "",
    `Use the 'name' attribute from the list above and generate a 'description' of about ${maxWords} words for each item.`,
    `Context: ${scope}`,
    `Style guidance for ${typeDef.name}: ${
      typeDef.prompt_for_description || typeDef.prompt || "(none provided)"
    }.`,
    "",
    "SYSTEM RULES:",
    "- Return ONLY a single JSON object that validates against the provided JSON Schema.",
    "- Only return fields defined by the schema.",
  ].join("\n")

  // JSON schema (client passes `parameters`; createResponse converts to `schema`)
  const parameters = {
    type: "object",
    properties: {
      descriptions: {
        type: "array",
        minItems: batch.length,
        maxItems: batch.length,
        items: {
          type: "object",
          properties: {
            name: {
              type: "string",
              enum: names,
              description: "The exact item name from the provided list",
            },
            description: {
              type: "string",
              description: `A concise description for this ${typeDef.name} item.`,
              minLength: Math.max(0, maxWords - 5) * AVERAGE_CHARS_PER_WORD,
              maxLength: maxWords * AVERAGE_CHARS_PER_WORD,
            },
          },
          required: ["name", "description"],
          additionalProperties: false,
        },
      },
    },
    required: ["descriptions"],
    additionalProperties: false,
  }

  const resp = await createResponse({
    prompt,
    model: "gpt-5-mini",
    jsonSchema: {
      name: "get_descriptions",
      description: `Generate descriptions for the provided ${typeDef.name} items.`,
      strict: true,
      parameters,
    },
    reasoning: { effort: "low" },
    store: false,
  })

  const raw = resp?.response?.output_text
  if (!raw) return []

  const obj = JSON.parse(raw)

  console.log("obj", obj)

  // Clean up any quoted names like `'Foo'` -> `Foo`
  obj.descriptions.forEach((item) => {
    if (
      typeof item.name === "string" &&
      item.name.startsWith("'") &&
      item.name.endsWith("'")
    ) {
      item.name = item.name.slice(1, -1)
    }
  })

  return obj.descriptions
}

const createElementDescriptions = async ({
  elements,
  handleUpdateDescriptions,
  setWaitingElementIds,
  scope,
  roles,
}) => {
  const batches = []
  console.log("%ccreateElementDescriptions: elements", "color:lightgreen", {
    elements,
  })
  const batchSize = 5
  for (let i = 0; i < elements.length; i += batchSize) {
    const batch = elements.slice(i, i + batchSize)
    batches.push(batch)
  }

  // elements grouped by their type attribute
  const elementsGroupedByType = elements.reduce((acc, curr) => {
    const type = curr.type
    if (!acc[type]) {
      acc[type] = []
    }
    acc[type].push(curr)
    return acc
  }, {})

  // Now split each type group into batches of 5

  const typeGroups = Object.keys(elementsGroupedByType)
  const typeBatches = typeGroups.reduce((acc, curr) => {
    const typeElements = elementsGroupedByType[curr]
    const typeBatches = []
    for (let i = 0; i < typeElements.length; i += batchSize) {
      const batch = typeElements.slice(i, i + batchSize)
      typeBatches.push(batch)
    }
    acc[curr] = typeBatches
    return acc
  }, {})

  console.log(
    "%ccreateElementDescriptions:typeBatches",
    "color:pink",
    typeBatches
  )

  // Consolidate all the type batches into a single array of batches

  const allTypeBatches = Object.values(typeBatches).reduce((acc, curr) => {
    acc.push(...curr)
    return acc
  }, [])

  console.log(
    "%ccreateElementDescriptions:allTypeBatches",
    "color:pink",
    allTypeBatches
  )

  allTypeBatches.reduce(async (prevPromise, batch) => {
    await prevPromise

    const typeDef = palette.getElementTypeByIndex(batch[0].type)
    const elementIds = batch.map((b) => b.id)
    setWaitingElementIds((curr) => [...curr, ...elementIds])

    //FIXME: this won't work if the model has a mix of element types, need batches grouped by type
    let jsonDescs = await getBatchDescription({
      scope,
      typeDef,
      batch,
      roles,
    })

    console.log(
      "%ccreateElementDescriptions:jsonDescs",
      "color:lightgreen",
      jsonDescs
    )

    if (jsonDescs.error) {
      return { error: jsonDescs.error }
    }

    // Sometimes the responses is an object, not an array. In this case, we need to convert it to an array
    if (!Array.isArray(jsonDescs)) {
      jsonDescs = Object.keys(jsonDescs).map((key) => ({
        name: key,
        ...jsonDescs[key],
      }))
    }

    setWaitingElementIds((curr) =>
      curr.filter((id) => !batch.map((b) => b.id).includes(id))
    )

    // Map jsonDescs to each batch elements 'description' attribute based on a name attribute match
    batch.forEach((element) => {
      const desc = jsonDescs.find(
        (jsonDesc) => jsonDesc.name.toLowerCase() === element.name.toLowerCase()
      )
      if (desc) {
        element.description = desc.description
      }
    })

    handleUpdateDescriptions({ jsonElements: batch })
    return batch
  }, Promise.resolve())
}

export {
  createContent,
  getAutoCountOfNextLevel,
  getPromptsForReferencedViews,
  createViewPrompt,
  loadDesignViewRefs,
  getViewReferences,
  parseViewReferences,
  getReferencedPromptViews,
  createElementDescriptions,
}
